import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { queryKeys } from '@/lib/data/keys';
import {
  getCompetition,
  getPlayer,
  getPlayerSeasonStats,
  getTeam,
  getTopPlayers,
  listAllTeams,
  listCompetitions,
  listFollowablePlayers,
  listCompetitionSeasonFixtures,
  listFixturesByCompetition,
  listTeamPlayers,
  listTeamsByCompetition,
} from '@/lib/data/provider';

export function useCompetitions() {
  return useQuery({
    queryKey: queryKeys.competitions,
    queryFn: listCompetitions,
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

export function useCompetition(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.competition(id ?? 'none'),
    queryFn: () => getCompetition(id as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(id),
  });
}

export function useCompetitionFixtures(
  competitionId: string | undefined,
  scope: 'upcoming' | 'results',
) {
  return useQuery({
    queryKey: queryKeys.fixturesByCompetition(competitionId ?? 'none', scope),
    queryFn: () => listFixturesByCompetition(competitionId as string, scope),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && Boolean(competitionId),
  });
}

export function useCompetitionSeasonFixtures(
  competitionId: string | undefined,
  season: number | undefined,
) {
  return useQuery({
    queryKey: queryKeys.competitionSeasonFixtures(competitionId ?? 'none', season ?? 0),
    queryFn: () => listCompetitionSeasonFixtures(competitionId as string, season as number),
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured && Boolean(competitionId) && season !== undefined,
  });
}

export function useTopPlayers(
  competitionId: string | undefined,
  season: number | undefined,
  statType: 'goals' | 'assists' = 'goals',
) {
  return useQuery({
    queryKey: [...queryKeys.topScorers(competitionId ?? 'none', season ?? 0), statType],
    queryFn: () => getTopPlayers(competitionId as string, season as number, 20, statType),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(competitionId) && season !== undefined,
  });
}

export function useAllTeams() {
  return useQuery({
    queryKey: queryKeys.allTeams,
    queryFn: () => listAllTeams(),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

/** Notable players for the onboarding "follow players" step. */
export function useFollowablePlayers() {
  return useQuery({
    queryKey: ['players', 'followable'],
    queryFn: () => listFollowablePlayers(40),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured,
  });
}

export function useTeams(competitionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.teams(competitionId ?? 'none'),
    queryFn: () => listTeamsByCompetition(competitionId as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(competitionId),
  });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.team(id ?? 'none'),
    queryFn: () => getTeam(id as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(id),
  });
}

export function useTeamPlayers(teamId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.teamPlayers(teamId ?? 'none'),
    queryFn: () => listTeamPlayers(teamId as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(teamId),
  });
}

export function usePlayer(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.player(id ?? 'none'),
    queryFn: () => getPlayer(id as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(id),
  });
}

export function usePlayerSeasonStats(playerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.playerSeason(playerId ?? 'none'),
    queryFn: () => getPlayerSeasonStats(playerId as string),
    staleTime: QUERY_STALE_TIME.reference,
    enabled: isSupabaseConfigured && Boolean(playerId),
  });
}
